
import React, { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Layers } from "lucide-react";
import NotificationTypeDropdown from "../NotificationTypeDropdown";
import OrderProductSelector, { SelectedOrderProduct } from "./OrderProductSelector";
import { useOrderProducts, OrderProduct } from "@/hooks/useOrderProducts";
import { useBulkOrderProducts, BulkOrderEntry } from "@/hooks/useBulkOrderProducts";
import BulkOrderCard from "../bulk/BulkOrderCard";
import BulkAutofillButtons from "../bulk/BulkAutofillButtons";
import { useAuth } from "@/context/AuthContext";

export const parseVoNumbers = (input: string): string[] => {
  return input
    .split(/[\s,;\n]+/)
    .map(s => s.trim())
    .filter(Boolean);
};

interface OrderDetailsProps {
  type: string;
  voNumber: string;
  exactOrderNumber: string;
  onTypeChange: (value: string) => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  selectedOrderProducts: SelectedOrderProduct[];
  isOverarchingNotification: boolean;
  onToggleOrderProduct: (product: OrderProduct) => void;
  onOrderProductMessageChange: (sku: string, message: string) => void;
  onOverarchingChange: (value: boolean) => void;
  onStatusChange?: (value: string) => void;
  onMessageChange?: (message: string) => void;
  // Bulk props
  bulkEntries: BulkOrderEntry[];
  isBulkFetching: boolean;
  bulkFetchProgress: { done: number; total: number };
  onBulkFetch: (voNumbers: string[], filterSuppliers?: boolean) => void;
  onBulkUpdateEntry: (voNumber: string, updates: Partial<BulkOrderEntry>) => void;
  onBulkUpdateAll: (updates: Partial<Pick<BulkOrderEntry, 'message' | 'type' | 'status'>>) => void;
  onBulkRemoveEntry: (voNumber: string) => void;
  onBulkClear: () => void;
}

const OrderDetails = ({ 
  type, 
  voNumber, 
  exactOrderNumber, 
  onTypeChange, 
  onInputChange,
  selectedOrderProducts,
  isOverarchingNotification,
  onToggleOrderProduct,
  onOrderProductMessageChange,
  onOverarchingChange,
  onStatusChange,
  onMessageChange,
  bulkEntries,
  isBulkFetching,
  bulkFetchProgress,
  onBulkFetch,
  onBulkUpdateEntry,
  onBulkUpdateAll,
  onBulkRemoveEntry,
  onBulkClear,
}: OrderDetailsProps) => {
  const { user } = useAuth();
  const isDecoprof = user?.company === "decoprof";
  const [activeAutofill, setActiveAutofill] = useState<string | null>(null);

  // Wrap onTypeChange to sync bulk entries bidirectionally
  const handleTypeChange = (value: string) => {
    onTypeChange(value);
    if (isBulk && bulkEntries.length > 0) {
      onBulkUpdateAll({ type: value });
    }
  };

  const parsedVoNumbers = useMemo(() => parseVoNumbers(voNumber), [voNumber]);
  const isBulk = parsedVoNumbers.length > 1;

  const singleVo = !isBulk ? parsedVoNumbers[0] || "" : "";

  // Single mode: use existing hook
  const { products, isLoading, error } = useOrderProducts(singleVo);

  const hasBulkFetched = bulkEntries.length > 0 && !isBulkFetching;

  // Auto-fetch when bulk VO numbers change (debounced)
  const voNumbersKey = parsedVoNumbers.join(',');
  React.useEffect(() => {
    if (!isBulk || parsedVoNumbers.length > 50) return;

    const timer = setTimeout(() => {
      setActiveAutofill(null);
      onBulkFetch(parsedVoNumbers, isDecoprof);
    }, 800);

    return () => clearTimeout(timer);
  }, [voNumbersKey, isBulk]);

  const handleAutofill = (opt: { message: string; type: string; status: string; label: string }) => {
    setActiveAutofill(opt.label);
    
    if (isBulk) {
      const voList = bulkEntries
        .filter(e => !e.error)
        .map(e => e.voNumber)
        .join(", ");
      
      const messageWithVo = opt.message
        ? `${opt.message} - ${voList}`
        : voList;
      
      onBulkUpdateAll({ message: messageWithVo, type: opt.type, status: opt.status });
    } else {
      // Single mode: set message via parent
      const messageWithVo = opt.message
        ? `${opt.message} - ${singleVo}`
        : singleVo;
      onMessageChange?.(messageWithVo);
      onStatusChange?.(opt.status);
    }
    onTypeChange(opt.type);
  };

  // Handle voNumber textarea change - create synthetic event
  const handleVoTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Reuse onInputChange with the textarea event
    onInputChange(e);
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <NotificationTypeDropdown 
          type={type} 
          onValueChange={handleTypeChange}
          required={true}
        />
        
        <div className="space-y-2">
          <Label htmlFor="voNumber">
            VO Nummer
          </Label>
          <Textarea
            id="voNumber"
            name="voNumber"
            placeholder="Bijv. 145146"
            value={voNumber}
            onChange={handleVoTextareaChange}
            className="min-h-[40px] resize-none"
            rows={isBulk ? 3 : 1}
          />
          {isBulk && (
            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
              <Layers className="h-3 w-3" />
              {parsedVoNumbers.length} VO-nummers gedetecteerd
            </Badge>
          )}
        </div>
      </div>

      {/* Bulk mode: loading progress */}
      {isBulk && isBulkFetching && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Ophalen...</span>
            <span>{bulkFetchProgress.done} / {bulkFetchProgress.total}</span>
          </div>
          <Progress value={(bulkFetchProgress.done / Math.max(bulkFetchProgress.total, 1)) * 100} />
        </div>
      )}

      {/* Bulk mode: fetched results */}
      {isBulk && hasBulkFetched && (
        <div className="space-y-4">
          {isDecoprof && (
            <BulkAutofillButtons activeLabel={activeAutofill} onSelect={handleAutofill} />
          )}

          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {bulkEntries.map((entry) => (
              <BulkOrderCard
                key={entry.voNumber}
                entry={entry}
                onMessageChange={(vo, msg) => onBulkUpdateEntry(vo, { message: msg })}
                onRemove={onBulkRemoveEntry}
              />
            ))}
          </div>

        </div>
      )}

      {/* Single mode: existing product selector */}
      {!isBulk && singleVo !== '' && (
        <>
          <OrderProductSelector
            products={products}
            isLoading={isLoading}
            error={error}
            selectedProducts={selectedOrderProducts}
            isOverarching={isOverarchingNotification}
            onToggleProduct={onToggleOrderProduct}
            onProductMessageChange={onOrderProductMessageChange}
            onOverarchingChange={onOverarchingChange}
          />
          {isDecoprof && products.length > 0 && (
            <BulkAutofillButtons activeLabel={activeAutofill} onSelect={handleAutofill} />
          )}
        </>
      )}
      
      {!isDecoprof && (
        <div className="space-y-2">
          <Label htmlFor="exactOrderNumber">Exact Ordernummer (optioneel)</Label>
          <Input
            id="exactOrderNumber"
            name="exactOrderNumber"
            placeholder="Bijv. 12345"
            value={exactOrderNumber}
            onChange={onInputChange}
          />
        </div>
      )}
    </>
  );
};

export default OrderDetails;
